import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Box, MenuItem, Select, Typography, RadioGroup, FormControlLabel, Radio, TextField, Button, CircularProgress, List, ListItemButton, Paper, ClickAwayListener } from "@mui/material";
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';

const CmLabel = (props) => {
    const { children, ...lProps } = props;
    return (
        <label style={{ color: '#6D7280', fontSize: '14px' }} {...lProps}>{children}</label>
    );
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const Step4 = ({ extrasMeetingPoint = [], meetingPointIncluded = null, selectedMeetingPoint = null, modalOpen = false, onMeetingPointChange = null }) => {
    const { t } = useTranslation("checkout");

    // Helper to extract a lat/lng from various shapes that might be present in your data
    const parseCoord = (obj) => {
        if (!obj) return null;
        const lat = obj.lat ?? obj.latitude ?? obj.latitud ?? null;
        const lng = obj.lon ?? obj.lng ?? obj.longitude ?? obj.long ?? null;
        if (lat == null || lng == null) return null;
        return { lat: Number(lat), lng: Number(lng) };
    };

    // fixed meeting point option from the tour (not the user's saved selection)
    const defaultMeetingPoint = useMemo(() => {
        if (Array.isArray(extrasMeetingPoint) && extrasMeetingPoint.length > 0) return extrasMeetingPoint[0];
        if (meetingPointIncluded && !Array.isArray(meetingPointIncluded)) return meetingPointIncluded;
        if (Array.isArray(meetingPointIncluded) && meetingPointIncluded.length > 0) return meetingPointIncluded[0];
        return null;
    }, [extrasMeetingPoint, meetingPointIncluded]);

    // user's saved selection (mode + address + coords)
    const selectedPoint = useMemo(() => {
        if (!selectedMeetingPoint) return null;
        if (Array.isArray(selectedMeetingPoint)) return selectedMeetingPoint[0] || null;
        return selectedMeetingPoint;
    }, [selectedMeetingPoint]);

    const initialCoord =
        parseCoord(selectedPoint) ||
        parseCoord(defaultMeetingPoint) ||
        { lat: 0, lng: 0 };

    const [mode, setMode] = useState(selectedPoint?.mode === 'hotel_pickup' ? 'hotel_pickup' : 'meeting_point'); // 'meeting_point' | 'hotel_pickup'
    const [hotelAddress, setHotelAddress] = useState(selectedPoint?.address || selectedPoint?.formatted_address || '');
    const [center, setCenter] = useState(initialCoord);
    const [marker, setMarker] = useState(initialCoord);
    const [geocoding, setGeocoding] = useState(false);
    const [predictions, setPredictions] = useState([]);
    const [showPredictions, setShowPredictions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const mapRef = useRef(null);
    const autocompleteServiceRef = useRef(null);
    const predictionsTimer = useRef(null);
    const lastMeetingEmitRef = useRef('');

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ['places']
    });

    const emitMeetingPointChange = useCallback((payload) => {
        if (typeof onMeetingPointChange !== 'function' || !payload) return;
        const normalized = {
            mode: payload?.mode || 'meeting_point',
            address: payload?.address || payload?.formatted_address || '',
            ...(payload?.placeId || payload?.place_id ? { placeId: payload?.placeId || payload?.place_id } : {}),
            ...(payload?.lat != null ? { lat: Number(payload.lat) } : {}),
            ...(payload?.lng != null ? { lng: Number(payload.lng) } : {}),
        };
        const fingerprint = JSON.stringify(normalized);
        if (lastMeetingEmitRef.current === fingerprint) return;
        lastMeetingEmitRef.current = fingerprint;
        onMeetingPointChange(normalized);
    }, [onMeetingPointChange]);

    useEffect(() => {
        // initialize form/map from saved selection when modal opens or selection changes
        const savedMode = selectedPoint?.mode === 'hotel_pickup' ? 'hotel_pickup' : 'meeting_point';
        setMode(savedMode);
        setHotelAddress(selectedPoint?.address || selectedPoint?.formatted_address || '');
        const coord = parseCoord(selectedPoint) || parseCoord(defaultMeetingPoint);
        if (coord) {
            setCenter(coord);
            setMarker(coord);
            if (mapRef.current) mapRef.current.panTo(coord);
        }
    }, [modalOpen, selectedPoint, defaultMeetingPoint]);

    useEffect(() => {
        if (mode !== 'meeting_point') return;
        const coord = parseCoord(defaultMeetingPoint);
        if (!coord) return;
        const payload = {
            mode: 'meeting_point',
            address: defaultMeetingPoint?.address || defaultMeetingPoint?.formatted_address || '',
            lat: coord.lat,
            lng: coord.lng
        };
        emitMeetingPointChange(payload);
    }, [mode, defaultMeetingPoint, emitMeetingPointChange]);

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    const doGeocode = async (address) => {
        if (!window.google || !window.google.maps) {
            return Promise.reject(new Error('Google Maps JS API not loaded'));
        }
        const geocoder = new window.google.maps.Geocoder();
        return new Promise((resolve, reject) => {
            geocoder.geocode({ address }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const loc = results[0].geometry.location;
                    const res = { lat: loc.lat(), lng: loc.lng(), formatted_address: results[0].formatted_address };
                    resolve(res);
                } else {
                    reject(new Error(status || 'Geocode failed'));
                }
            });
        });
    };

    const geocodePlaceId = async (placeId) => {
        if (!window.google || !window.google.maps) {
            return Promise.reject(new Error('Google Maps JS API not loaded'));
        }
        const geocoder = new window.google.maps.Geocoder();
        return new Promise((resolve, reject) => {
            geocoder.geocode({ placeId }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const loc = results[0].geometry.location;
                    const res = { lat: loc.lat(), lng: loc.lng(), formatted_address: results[0].formatted_address };
                    resolve(res);
                } else {
                    reject(new Error(status || 'Geocode by placeId failed'));
                }
            });
        });
    };

    useEffect(() => {
        if (!isLoaded) return;
        if (!window.google || !window.google.maps) return;
        if (!autocompleteServiceRef.current) autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }, [isLoaded]);

    useEffect(() => {
        // fetch predictions when hotelAddress changes (debounced)
        if (!hotelAddress || hotelAddress.trim().length === 0) {
            setPredictions([]);
            setShowPredictions(false);
            return;
        }
        if (!isLoaded || !autocompleteServiceRef.current) return;

        if (predictionsTimer.current) clearTimeout(predictionsTimer.current);
        predictionsTimer.current = setTimeout(() => {
            autocompleteServiceRef.current.getPlacePredictions({ input: hotelAddress }, (preds, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) {
                    setPredictions(preds);
                    setShowPredictions(true);
                } else {
                    setPredictions([]);
                    setShowPredictions(false);
                }
            });
        }, 300);

        return () => {
            if (predictionsTimer.current) clearTimeout(predictionsTimer.current);
        };
    }, [hotelAddress, isLoaded]);

    const handleGeocodeAddress = async () => {
        if (!hotelAddress) return;
        setGeocoding(true);
        try {
            const result = await doGeocode(hotelAddress);
            setCenter({ lat: result.lat, lng: result.lng });
            setMarker({ lat: result.lat, lng: result.lng });
            // pan the map if available
            if (mapRef.current) mapRef.current.panTo({ lat: result.lat, lng: result.lng });
        } catch (e) {
            // could surface error to user in a toast; for now just console
            console.error('Geocode error', e);
        } finally {
            setGeocoding(false);
        }
    };


    return (
        <>
            <Typography
                variant="h5"
                sx={{
                    fontSize: '20px',
                    color: 'secondary.main'
                }}
            >
                {t('update-tour-step4-title')}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    width: '100%',
                    py: '8px',
                    gap: 1
                }}
            >
                <CmLabel htmlFor="assistant">{t('update-tour-step4-how-arrive')}</CmLabel>

                <RadioGroup
                    row
                    value={mode}
                    onChange={(e) => {
                        const nextMode = e.target.value;
                        setMode(nextMode);
                        if (nextMode === 'hotel_pickup') {
                            emitMeetingPointChange({
                                mode: 'hotel_pickup',
                                address: '',
                                lat: null,
                                lng: null,
                                placeId: selectedPoint?.placeId || selectedPoint?.place_id
                            });
                        }
                    }}
                >
                    <FormControlLabel value="meeting_point" control={<Radio />} label={t('update-tour-step4-meeting-point-option')} />
                    <FormControlLabel value="hotel_pickup" control={<Radio />} label={t('update-tour-step4-hotel-pickup-option')} />
                </RadioGroup>

                {mode === 'hotel_pickup' && (
                    <ClickAwayListener onClickAway={() => setShowPredictions(false)}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Box sx={{ width: '100%' }}>
                                {/* <CmLabel htmlFor="hotelAddress">Escribe la dirección de tu hotel</CmLabel> */}
                                <TextField
                                    fullWidth
                                    label={t('update-tour-step4-hotel-address-label')}
                                    value={hotelAddress}
                                    onChange={(e) => { setHotelAddress(e.target.value); setHighlightedIndex(-1); }}
                                    onFocus={() => { if (predictions && predictions.length > 0) setShowPredictions(true); }}
                                    onKeyDown={async (e) => {
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setHighlightedIndex(i => Math.min((predictions?.length || 0) - 1, Math.max(0, i + 1)));
                                            setShowPredictions(true);
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setHighlightedIndex(i => Math.max(0, i - 1));
                                        } else if (e.key === 'Enter') {
                                            if (highlightedIndex >= 0 && predictions && predictions[highlightedIndex]) {
                                                const p = predictions[highlightedIndex];
                                                e.preventDefault();
                                                try {
                                                    setGeocoding(true);
                                                    const res = await geocodePlaceId(p.place_id);
                                                    setHotelAddress(p.description);
                                                    setCenter({ lat: res.lat, lng: res.lng });
                                                    setMarker({ lat: res.lat, lng: res.lng });
                                                    if (mapRef.current) mapRef.current.panTo({ lat: res.lat, lng: res.lng });
                                                    setPredictions([]);
                                                    setShowPredictions(false);
                                                    emitMeetingPointChange({ mode: 'hotel_pickup', address: p.description, lat: res.lat, lng: res.lng, placeId: p.place_id });
                                                } catch (err) {
                                                    console.error('Place geocode error', err);
                                                } finally {
                                                    setGeocoding(false);
                                                }
                                            } else {
                                                // if Enter and no highlighted suggestion, fallback to geocode by text
                                                e.preventDefault();
                                                try {
                                                    setGeocoding(true);
                                                    const res = await doGeocode(hotelAddress);
                                                    setCenter({ lat: res.lat, lng: res.lng });
                                                    setMarker({ lat: res.lat, lng: res.lng });
                                                    if (mapRef.current) mapRef.current.panTo({ lat: res.lat, lng: res.lng });
                                                    emitMeetingPointChange({ mode: 'hotel_pickup', address: hotelAddress, lat: res.lat, lng: res.lng });
                                                } catch (err) {
                                                    console.error('Geocode error', err);
                                                } finally {
                                                    setGeocoding(false);
                                                }
                                            }
                                        } else if (e.key === 'Escape') {
                                            setShowPredictions(false);
                                            setHighlightedIndex(-1);
                                        }
                                    }}
                                />
                                {showPredictions && predictions && predictions.length > 0 && (
                                    <Paper elevation={3} sx={{ position: 'absolute', zIndex: 9999, mt: 1, width: 'calc(100% - 32px)' }}>
                                        <List dense>
                                            {predictions.map((p, idx) => (
                                                <ListItemButton
                                                    key={p.place_id}
                                                    selected={idx === highlightedIndex}
                                                    onMouseEnter={() => setHighlightedIndex(idx)}
                                                    onClick={async () => {
                                                        try {
                                                            setGeocoding(true);
                                                            const res = await geocodePlaceId(p.place_id);
                                                            setHotelAddress(p.description);
                                                            setCenter({ lat: res.lat, lng: res.lng });
                                                            setMarker({ lat: res.lat, lng: res.lng });
                                                            if (mapRef.current) mapRef.current.panTo({ lat: res.lat, lng: res.lng });
                                                            setPredictions([]);
                                                            setShowPredictions(false);
                                                            emitMeetingPointChange({ mode: 'hotel_pickup', address: p.description, lat: res.lat, lng: res.lng, placeId: p.place_id });
                                                        } catch (err) {
                                                            console.error('Place geocode error', err);
                                                        } finally {
                                                            setGeocoding(false);
                                                        }
                                                    }}
                                                >{p.description}</ListItemButton>
                                            ))}
                                        </List>
                                    </Paper>
                                )}
                            </Box>
                        </Box>
                    </ClickAwayListener>
                )}

                <Box sx={{ width: '100%', height: 180, mt: 1 }}>
                    {!isLoaded && !loadError && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {loadError && (
                        <Box sx={{ color: 'error.main' }}>{t('update-tour-step4-map-error')}</Box>
                    )}
                    {isLoaded && !loadError && (
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={center}
                            zoom={center.lat === 0 && center.lng === 0 ? 2 : 15}
                            onLoad={onMapLoad}
                        >
                            <Marker position={marker} />
                        </GoogleMap>
                    )}
                </Box>

                {mode === 'meeting_point' && defaultMeetingPoint && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">{t('update-tour-step4-meeting-point-label')}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{defaultMeetingPoint.address || defaultMeetingPoint.formatted_address || `${defaultMeetingPoint.lat || defaultMeetingPoint.latitude || ''}, ${defaultMeetingPoint.lon || defaultMeetingPoint.lng || ''}`}</Typography>
                    </Box>
                )}
            </Box>
        </>
    )
}

export default Step4;
